import { toCents } from "./money";
import type { Money } from "./types";

/**
 * Gastos' search box, read the way the API reads it (`services/tx_search.parse_search`), so a
 * quick add shows in the list it belongs to: an amount ("850", "$1,234.50"), a range
 * ("800-900", "800 a 900") or words that must all be in the description, accents and case aside.
 */
export type TxSearch =
  | { kind: "all" }
  | { kind: "words"; words: string[] }
  | { kind: "amount"; low: number; high: number };

const NUMBER = String.raw`\$?\s*(\d{1,3}(?:,\d{3})+(?:\.\d{1,2})?|\d+(?:\.\d{1,2})?)`;
const AMOUNT = new RegExp(String.raw`^${NUMBER}$`);
const RANGE = new RegExp(String.raw`^${NUMBER}\s*(?:-|–|—|\ba\b)\s*${NUMBER}$`);

/** Accents and case aside, like the API's `labels.sort_key`. */
export const searchKey = (text: string): string =>
  text.normalize("NFKD").replace(/[̀-ͯ]/g, "").toLowerCase();

/** "850" means any amount in pesos 850: $850.00 to $850.99. */
const bounds = (text: string): [number, number] => {
  const cents = toCents(text);
  return [cents, text.includes(".") ? cents : cents + 99];
};

export function parseSearch(raw: string | undefined): TxSearch {
  const text = (raw ?? "").trim();
  if (!text) return { kind: "all" };
  const range = RANGE.exec(text);
  if (range?.[1] && range[2]) {
    const [a, aTop] = bounds(range[1]);
    const [b, bTop] = bounds(range[2]);
    return a <= b ? { kind: "amount", low: a, high: bTop } : { kind: "amount", low: b, high: aTop };
  }
  const amount = AMOUNT.exec(text);
  if (amount?.[1]) {
    const [low, high] = bounds(amount[1]);
    return { kind: "amount", low, high };
  }
  return { kind: "words", words: searchKey(text).split(/\s+/) };
}

/** Amounts are compared without their sign: a refund of $850 matches "850". */
export function searchMatches(search: TxSearch, description: string, amount: Money): boolean {
  if (search.kind === "all") return true;
  if (search.kind === "amount") {
    const cents = Math.abs(toCents(amount));
    return cents >= search.low && cents <= search.high;
  }
  const key = searchKey(description);
  return search.words.every((w) => key.includes(w));
}

/** What the search box hints at. */
export const SEARCH_PLACEHOLDER = "Buscar o monto: 850, 800-900";
